import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'src/hooks/use-debounce';
import {
  isPortalSyncNotConfiguredError,
  useBlockPortalUserMutation,
  useGetPortalUsersQuery,
  useSetPortalUserQuotaOverrideMutation,
  useUnblockPortalUserMutation,
  useUpdatePortalSubscriptionTierMutation,
} from '../endpoints/portal-users.endpoints';
import type {
  AdminSettableSubscriptionTier,
  PortalUser,
  PortalUserStatus,
  PortalUserSubscriptionTier,
  StatusFilter,
  SubscriptionFilter,
} from '../types/users.types';

const DEFAULT_PAGE_SIZE = 25;

export function useFlowUsers() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState<SubscriptionFilter>('all');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 500);

  const queryParams = useMemo(() => ({
    search: debouncedSearch.trim() || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    subscription: subscriptionFilter === 'all' ? undefined : subscriptionFilter,
    page,
    page_size: DEFAULT_PAGE_SIZE,
  }), [debouncedSearch, statusFilter, subscriptionFilter, page]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetPortalUsersQuery(queryParams);

  const [blockUser] = useBlockPortalUserMutation();
  const [unblockUser] = useUnblockPortalUserMutation();
  const [setQuotaOverride] = useSetPortalUserQuotaOverrideMutation();
  const [updateSubscriptionTier] = useUpdatePortalSubscriptionTierMutation();

  const [optimisticUsers, setOptimisticUsers] = useState<Record<string, PortalUser>>({});

  const users = useMemo(() => {
    const results = data?.results ?? [];
    return results.map((user) => optimisticUsers[user.id] ?? user);
  }, [data?.results, optimisticUsers]);

  const isPortalSyncNotConfigured = isError && isPortalSyncNotConfiguredError(error);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((value: StatusFilter) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const handleSubscriptionFilterChange = useCallback((value: SubscriptionFilter) => {
    setSubscriptionFilter(value);
    setPage(1);
  }, []);

  const applyBlockAction = useCallback(async(userId: string) => {
    const previous = users.find((user) => user.id === userId);
    if (!previous) {
      return;
    }

    const optimistic: PortalUser = {
      ...previous,
      is_blocked: true,
      status: 'blocked',
    };
    setOptimisticUsers((current) => ({ ...current, [userId]: optimistic }));

    try {
      const updated = await blockUser(userId).unwrap();
      setOptimisticUsers((current) => ({ ...current, [userId]: updated }));
    } catch {
      setOptimisticUsers((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
    }
  }, [blockUser, users]);

  const applyUnblockAction = useCallback(async(userId: string) => {
    const previous = users.find((user) => user.id === userId);
    if (!previous) {
      return;
    }

    const optimistic: PortalUser = {
      ...previous,
      is_blocked: false,
      status: 'active',
    };
    setOptimisticUsers((current) => ({ ...current, [userId]: optimistic }));

    try {
      const updated = await unblockUser(userId).unwrap();
      setOptimisticUsers((current) => ({ ...current, [userId]: updated }));
    } catch {
      setOptimisticUsers((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
    }
  }, [unblockUser, users]);

  const saveQuotaOverride = useCallback(async(userId: string, value: number | null) => {
    const previous = users.find((user) => user.id === userId);
    if (!previous) {
      return { ok: false as const, message: 'User not found.' };
    }

    const optimistic: PortalUser = {
      ...previous,
      creative_quota_override: value,
      monthly_limit: value ?? previous.monthly_limit,
    };
    setOptimisticUsers((current) => ({ ...current, [userId]: optimistic }));

    try {
      const updated = await setQuotaOverride({ userId, creative_quota_override: value }).unwrap();
      setOptimisticUsers((current) => ({ ...current, [userId]: updated }));
      return { ok: true as const };
    } catch (mutationError: unknown) {
      setOptimisticUsers((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
      const responseData = (mutationError as { responseData?: Record<string, unknown> })?.responseData;
      const detail = typeof responseData?.detail === 'string' ? responseData.detail : 'Failed to save quota override.';
      return { ok: false as const, message: detail };
    }
  }, [setQuotaOverride, users]);

  const saveSubscriptionTier = useCallback(async(
    workspaceId: string,
    tier: AdminSettableSubscriptionTier
  ) => {
    const affectedUsers = users.filter((user) => user.workspace_id === workspaceId);
    if (affectedUsers.length === 0) {
      return { ok: false as const, message: 'No users found for this workspace.' };
    }

    const optimisticTier = tier as PortalUserSubscriptionTier;
    setOptimisticUsers((current) => {
      const next = { ...current };
      affectedUsers.forEach((user) => {
        next[user.id] = { ...user, subscription: optimisticTier };
      });
      return next;
    });

    try {
      await updateSubscriptionTier({ workspaceId, tier: optimisticTier }).unwrap();
      return { ok: true as const };
    } catch (mutationError: unknown) {
      setOptimisticUsers((current) => {
        const next = { ...current };
        affectedUsers.forEach((user) => {
          delete next[user.id];
        });
        return next;
      });
      const responseData = (mutationError as { responseData?: Record<string, unknown> })?.responseData;
      const message = typeof responseData?.message === 'string'
        ? responseData.message
        : typeof responseData?.detail === 'string'
          ? responseData.detail
          : 'Failed to update subscription tier.';
      return { ok: false as const, message };
    }
  }, [updateSubscriptionTier, users]);

  return {
    users,
    totalCount: data?.count ?? 0,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    setPage,
    search,
    setSearch: handleSearchChange,
    statusFilter,
    setStatusFilter: handleStatusFilterChange,
    subscriptionFilter,
    setSubscriptionFilter: handleSubscriptionFilterChange,
    isLoading: isLoading || isFetching,
    isError,
    isPortalSyncNotConfigured,
    error,
    refetch,
    applyBlockAction,
    applyUnblockAction,
    saveQuotaOverride,
    saveSubscriptionTier,
    actionsDisabled: isPortalSyncNotConfigured,
  };
}

export type { PortalUserStatus, SubscriptionFilter };
